import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, resolveMediaUrl } from "@/lib/api";
import { Loader2, Trash2, Upload, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const CATEGORIES = ["detailing", "pressure_washing", "marine", "before_after"];

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addUrl, setAddUrl] = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addCat, setAddCat] = useState("detailing");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery");
      setImages(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addImage = async (url) => {
    setAdding(true);
    try {
      const { data } = await api.post("/admin/gallery", {
        url,
        title: addTitle || undefined,
        category: addCat,
      });
      setImages((s) => [data, ...s]);
      setAddUrl("");
      setAddTitle("");
      toast.success("Image added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  const uploadFile = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await addImage(data.url);
      setFile(null);
      const input = document.querySelector(`[data-testid="${ADMIN.galleryUploadFile}"]`);
      if (input) input.value = "";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setImages((s) => s.filter((i) => i.id !== id));
      toast.success("Image deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-8 bg-cyan-400" />
          <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
            Media
          </span>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tighter">
          Gallery Manager
        </h1>
        <p className="text-zinc-400 mt-2">Add, upload, or remove gallery images shown on the site.</p>
      </div>

      {/* Add via URL */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-6">
          <h3 className="text-white font-heading text-lg uppercase tracking-tight mb-4">
            Add from URL
          </h3>
          <div className="space-y-3">
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Image URL</Label>
              <Input
                data-testid={ADMIN.galleryAddUrl}
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                placeholder="https://..."
                className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-11 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase tracking-widest text-xs text-zinc-400">Title</Label>
                <Input
                  data-testid={ADMIN.galleryAddTitle}
                  value={addTitle}
                  onChange={(e) => setAddTitle(e.target.value)}
                  placeholder="Optional"
                  className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-11 mt-1"
                />
              </div>
              <div>
                <Label className="uppercase tracking-widest text-xs text-zinc-400">Category</Label>
                <Select value={addCat} onValueChange={setAddCat}>
                  <SelectTrigger
                    data-testid={ADMIN.galleryAddCategory}
                    className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-11 mt-1"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} className="text-white focus:bg-cyan-400/10 focus:text-cyan-400">
                        {c.replace("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={() => addUrl && addImage(addUrl)}
              disabled={!addUrl || adding}
              data-testid={ADMIN.galleryAddSubmit}
              className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-xs h-11 w-full mt-2"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" />Add Image</>}
            </Button>
          </div>
        </div>

        {/* Upload */}
        <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-6">
          <h3 className="text-white font-heading text-lg uppercase tracking-tight mb-4">
            Upload from device
          </h3>
          <div className="space-y-3">
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Image file (JPG, PNG, WEBP)</Label>
              <input
                type="file"
                accept="image/*"
                data-testid={ADMIN.galleryUploadFile}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-2 w-full text-sm text-zinc-300 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-cyan-400 file:text-black file:uppercase file:tracking-widest file:text-xs file:font-bold file:cursor-pointer hover:file:bg-cyan-300"
              />
            </div>
            <Button
              onClick={uploadFile}
              disabled={!file || uploading}
              data-testid={ADMIN.galleryUploadSubmit}
              className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-xs h-11 w-full"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" />Upload &amp; Add</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Existing images */}
      <h3 className="text-white font-heading text-lg uppercase tracking-tight mb-4">Current images</h3>
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading...
        </div>
      ) : images.length === 0 ? (
        <div className="border border-white/10 bg-white/[0.02] p-10 text-zinc-400 text-center">
          No images yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <div key={img.id} className="relative group border border-white/10 bg-white/[0.02]">
              <img
                src={resolveMediaUrl(img.url)}
                alt={img.title || "Gallery"}
                className="w-full aspect-square object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(img.id)}
                  data-testid={ADMIN.galleryDelete(img.id)}
                  className="rounded-none border-red-400/60 bg-red-500/10 hover:bg-red-500/20 text-red-300"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Delete
                </Button>
              </div>
              {img.title && (
                <div className="p-2 text-xs text-zinc-300 truncate">{img.title}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
